import React, { useState } from 'react'
import { Mail, User } from 'lucide-react'
import { Button } from './ui/button'
import { api } from '../services/api'

const NewsletterSignup: React.FC = () => {
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!firstName.trim() || !email.trim()) {
      setMessage('Please fill in all required fields')
      setIsSuccess(false)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address')
      setIsSuccess(false)
      return
    }

    try {
      setIsSubmitting(true)
      setMessage('')
      
      const response = await api.post('/newsletter/subscribe', {
        firstName: firstName.trim(),
        email: email.trim()
      })

      if (response.data.success) {
        setMessage('Welcome to the Excel Nerds community! Check your email for confirmation.')
        setIsSuccess(true)
        setFirstName('')
        setEmail('')
      } else {
        setMessage(response.data.message || 'Something went wrong. Please try again.')
        setIsSuccess(false)
      }
    } catch (error: any) {
      console.error('Newsletter signup error:', error)
      setMessage(error.response?.data?.message || 'Failed to subscribe. Please try again.')
      setIsSuccess(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="bg-gradient-to-br from-excel-green to-emerald-600 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center text-white">
          {/* Header */}
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Get Smarter in Excel - One Tip at a Time
          </h2>
          <p className="text-xl mb-8 text-emerald-50 leading-relaxed">
            Join hundreds of learners getting free Excel tips, tricks, and news straight to their inbox. 
            Practical, bite-sized, and easy to use.
          </p>

          {/* Newsletter Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow-xl">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {/* First Name Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-excel-green focus:border-transparent text-gray-900 placeholder-gray-500"
                />
              </div>

              {/* Email Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-excel-green focus:border-transparent text-gray-900 placeholder-gray-500"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 text-lg font-semibold bg-excel-green hover:bg-green-700 text-white border-0 rounded-lg transition-colors duration-200"
            >
              {isSubmitting ? 'Joining...' : 'Join the Nerds'}
            </Button>

            {/* Status Message */}
            {message && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${
                isSuccess 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {message}
              </div>
            )}
          </form>

          {/* Privacy Note */}
          <p className="text-sm text-emerald-100 mt-4">
            No spam, ever. Unsubscribe anytime with one click.
          </p>
        </div>
      </div>
    </section>
  )
}

export default NewsletterSignup